import type { ViewerTopNavModel } from "./ViewerTopNav";

/**
 * Imperative ↔ React bridge for the viewer's top nav.
 *
 * The viewer's main orchestration ([src/main.ts](../../main.ts)) is plain TS
 * — it builds a `ViewerTopNavModel` from the live siteConfig + DEM tier +
 * report token state and used to call `viewerTopNavController.bump()` to
 * trigger a re-render of an imperatively-mounted React root. Phase 1 hoists
 * the viewer top nav into the persistent RootLayout shell, which means
 * `main.ts` can no longer reach into a React root directly.
 *
 * This module owns one module-scope publisher per concern (the current model
 * + the export button handler) so `main.ts` can keep its "fire-and-forget"
 * ergonomics — it doesn't need to know whether anything is listening yet,
 * or what React state shape is wired up downstream. RootLayout registers a
 * fresh publisher whenever the viewer route mounts; the registration returns
 * a teardown that flips back to a no-op when the viewer route unmounts.
 *
 * The publisher is intentionally a singleton (not a context): the model
 * lives "above" the route boundary so the persistent header can read it
 * without the route being in the tree. Only one viewer route is ever active
 * at a time, so a single bridge is sufficient.
 */

/** Active Create Illustration framing — reuses the persistent top nav shell. */
export interface ViewerIllustrationCaptureNav {
  onCancel: () => void;
  onContinue: () => void;
}

export interface ViewerTopNavBridge {
  setModel: (model: ViewerTopNavModel | null) => void;
  setExportHandler: (handler: (() => void) | null) => void;
  setPdfHandler: (handler: (() => void) | null) => void;
  setIllustrationCapture: (nav: ViewerIllustrationCaptureNav | null) => void;
}

const noopBridge: ViewerTopNavBridge = {
  setModel: () => {},
  setExportHandler: () => {},
  setPdfHandler: () => {},
  setIllustrationCapture: () => {}
};

let activeBridge: ViewerTopNavBridge = noopBridge;

/**
 * Wire React state setters into the bridge. RootLayout calls this on mount
 * and disposes via the returned teardown on unmount.
 *
 * Replacing an already-active bridge falls back to the noop if the SAME
 * registration is the one being torn down — preventing a late teardown from
 * a stale RootLayout instance from clobbering a fresh registration in case
 * React 18's StrictMode double-mount ever gets re-enabled.
 */
export function registerViewerTopNavBridge(
  bridge: ViewerTopNavBridge
): () => void {
  activeBridge = bridge;
  return () => {
    if (activeBridge === bridge) {
      activeBridge = noopBridge;
    }
  };
}

/**
 * Push a new model from imperative code (typically `src/main.ts`'s
 * `republishViewerTopNavModel()`). The publisher is a no-op when the
 * viewer route isn't mounted, which is safe — the model would be discarded
 * anyway since there's nothing to render it.
 */
export function publishViewerTopNavModel(model: ViewerTopNavModel | null): void {
  activeBridge.setModel(model);
}

/**
 * Wire the export-screenshot action from `src/main.ts` into the header's
 * Export button. Same lifecycle as the model — pushed in by main.ts after
 * the renderer is up; flushed back to null when the viewer route unmounts.
 */
export function publishViewerTopNavExportHandler(
  handler: (() => void) | null
): void {
  activeBridge.setExportHandler(handler);
}

/**
 * Wire the PDF-report export action from `src/main.ts` into the header's
 * Export menu "Download PDF" item. Same lifecycle as the screenshot handler:
 * pushed in by main.ts once the renderer + analysis data are ready; flushed
 * back to null when the viewer route unmounts.
 *
 * Until this handler is published, the Export menu still renders the PDF
 * option, but clicking it is a no-op — matches existing patterns where
 * boot-pending actions silently drop instead of throwing.
 */
export function publishViewerTopNavPdfHandler(
  handler: (() => void) | null
): void {
  activeBridge.setPdfHandler(handler);
}

/**
 * Swap the viewer top nav into Create Illustration framing chrome
 * (transparent bar, close + Continue). Pass `null` on exit.
 */
export function publishViewerIllustrationCaptureNav(
  nav: ViewerIllustrationCaptureNav | null
): void {
  activeBridge.setIllustrationCapture(nav);
}
